// Single source of truth for sidebar menu structure and per-role access.
//
// Used by:
//  - components/common/LeftSideBar.js  -> render menu + filter by access
//  - actions/auth.js                   -> pick landing route after login
//  - containers/PrivateRoute.js        -> gate a route by menu id
//
// To hide a menu from a role, remove its id from that role's access array.
// To hide it from everyone, remove the item from MENU_ITEMS.

export const MENU_ITEMS = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: "fa fa-dashboard",
    url: "/",
    exact: true,
  },
  {
    // Landing page for roles without dashboard access. `hidden` keeps it out of
    // the sidebar, but it still works as a route and as a landing target.
    id: "welcome",
    title: "Welcome",
    icon: "fa fa-home",
    url: "/welcome",
    exact: true,
    hidden: true,
  },
  {
    id: "questionnaires",
    title: "Questionnaires",
    icon: "fa fa-file-o",
    url: "/questionnaires",
    children: [
      {
        id: "newQuestionnaire",
        title: "New Questionnaire",
        url: "/questionnaires/new",
      },
      {
        id: "questionnaireList",
        title: "Questionnaire List",
        url: "/questionnaires",
      },
      {
        id: "quotationList",
        title: "Quotation List",
        url: "/quotations",
      },
      {
        id: "surveillanceList",
        title: "Surveillance List",
        url: "/quotation-schedules?phase=surveillance",
      },
      {
        id: "recertificationList",
        title: "Recertification List",
        url: "/quotation-schedules?phase=recertification",
      },
    ],
  },
  {
    id: "orders",
    title: "Orders",
    icon: "fa fa-file",
    url: "/orders",
    children: [
      {
        id: "orderList",
        title: "Order List",
        url: "/orders",
      },
      {
        id: "auditPlanList",
        title: "Audit Plan List",
        url: "/audit-plans",
      },
      {
        id: "auditReportList",
        title: "Audit Report List",
        url: "/audit-reports",
      },
      {
        id: "nonComplyList",
        title: "Non Conformity List",
        url: "/ncs",
      },
      {
        id: "certificateList",
        title: "Certificate List",
        url: "/certificates",
      },
      {
        id: "daList",
        title: "Daily Allowance List",
        url: "/daily-allowances",
      },
      {
        id: "daPaymentList",
        title: "Daily Allowance Payment List",
        url: "/daily-allowance-payments",
      },
    ],
  },
  {
    id: "invoices",
    title: "Invoices",
    icon: "fa fa-bank",
    url: "/invoices",
    children: [
      {
        id: "invoiceList",
        title: "Invoice List",
        url: "/invoices",
      },
    ],
  },
  {
    id: "scheduleMatrix",
    title: "Schedule Matrix",
    url: "/schedule-matrix",
    icon: "fa fa-table",
  },
  {
    id: "customers",
    title: "Customers",
    icon: "fa fa-user-circle",
    url: "/customers",
  },
  {
    id: "users",
    title: "Users",
    icon: "fa fa-group",
    url: "/users",
    children: [
      {
        id: "userList",
        title: "User List",
        url: "/users",
      },
      {
        id: "auditorExperts",
        title: "Auditor & Experts",
        url: "/users/auditor-experts",
      },
    ],
  },
  {
    id: "standards",
    title: "Standards",
    icon: "fa fa-book",
    url: "/standards",
  },
  {
    id: "settings",
    title: "Settings",
    icon: "fa fa-cogs",
    url: "/settings",
    children: [
      {
        id: "events",
        title: "Events",
        icon: "fa fa-calendar",
        url: "/events",
      },
      {
        id: "options",
        title: "Options",
        icon: "fa fa-cogs",
        url: "/options",
      },
    ],
  },
  {
    // Opens an external app via token exchange (no local route) -> skipped as a landing target.
    id: "advance-scheduling",
    title: "Advance Scheduling",
    icon: "fa fa-calendar",
    url: "/advance-scheduling",
    external: true,
  },
  {
    // Opens an external app via token exchange (no local route) -> skipped as a landing target.
    id: "audit-execution",
    title: "Audit Execution Module",
    icon: "fa fa-magic",
    url: "/audit-execution",
    external: true,
  },
];

// Access lists per role. Each entry is a list of menu ids that role may see.
const customerAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "invoices",
  "invoiceList",
];
const salesAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "certificateList",
  "customers",
  "scheduleMatrix",
];
const salesManagerAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "certificateList",
  "customers",
  "scheduleMatrix",
];
const applicationReviewerManagerAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  // 'daPaymentList',
  "scheduleMatrix",
];
const auditorAccess = [
  // Auditors reach the dashboard, but only see the SLA and Top Customers /
  // Scheme panels -- see config/dashboardAccess.js.
  "dashboard",
  "welcome",
  "questionnaires",
  "questionnaireList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  // 'daPaymentList',
  "scheduleMatrix",
  "audit-execution",
];
const bodAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "scheduleMatrix",
];
const schedulerAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "orders",
  "orderList",
  "scheduleMatrix",
  "advance-scheduling",
  "audit-execution",
];
const headOfCBAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  "daPaymentList",
  "scheduleMatrix",
];
const deputyHeadOfCBAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  "daPaymentList",
  "scheduleMatrix",
];
const auditPlanOperatorAccess = [
  "dashboard",
  "orders",
  "orderList",
  "auditPlanList",
];
const financeAccess = [
  "dashboard",
  "questionnaires",
  "quotationList",
  "orders",
  "orderList",
  "daList",
  "daPaymentList",
  "invoices",
  "invoiceList",
  "audit-execution",
];
const certificateAdminAccess = [
  "dashboard",
  "orders",
  "orderList",
  "certificateList",
];
const superAdminAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  "daPaymentList",
  "invoices",
  "invoiceList",
  "scheduleMatrix",
  "customers",
  "users",
  "userList",
  "auditorExperts",
  "standards",
  "settings",
  "events",
  "options",
  "advance-scheduling",
  "audit-execution",
];
const adminSupportAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  "daPaymentList",
  "invoices",
  "invoiceList",
  "scheduleMatrix",
  "customers",
  "users",
  "userList",
  "auditorExperts",
  "standards",
  "settings",
  "events",
  "options",
];
const surveillanceAdminAccess = [
  "dashboard",
  "questionnaires",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
];
const accountExecutiveAccess = [
  "dashboard",
  "questionnaires",
  "newQuestionnaire",
  "questionnaireList",
  "quotationList",
  "surveillanceList",
  "recertificationList",
  "orders",
  "orderList",
  "auditPlanList",
  "auditReportList",
  "nonComplyList",
  "certificateList",
  "daList",
  "daPaymentList",
  "invoices",
  "invoiceList",
  "scheduleMatrix",
  "customers",
  "users", // (view only, tidak bisa create & update)
  "auditorExperts",
  "standards",
  "settings",
  "events",
  "options",
  "audit-execution",
];
const generalAffairAccess = ["dashboard", "audit-execution"];

// Role name -> access list. A user may hold several roles; access is the union.
// Note: "Operational Manager" intentionally reuses the Super Admin access list.
const ROLE_ACCESS = [
  ["Customer", customerAccess],
  ["Sales", salesAccess],
  ["Sales Manager", salesManagerAccess],
  ["Application Reviewer Coordinator", applicationReviewerManagerAccess],
  ["Auditor", auditorAccess],
  ["Board of Director", bodAccess],
  ["Scheduler", schedulerAccess],
  ["Head of CB", headOfCBAccess],
  ["Deputy Head of CB", deputyHeadOfCBAccess],
  ["Audit Plan Operator", auditPlanOperatorAccess],
  ["Super Admin", superAdminAccess],
  ["Admin Support", adminSupportAccess],
  ["Operational Manager", superAdminAccess],
  ["Finance", financeAccess],
  ["Certificate Admin", certificateAdminAccess],
  ["Surveillance Admin", surveillanceAdminAccess],
  ["Account Executive", accountExecutiveAccess],
  ["General Affair", generalAffairAccess],
];

// Normalize the role value coming from the store (array of role names).
function normalizeRoles(roles) {
  if (Array.isArray(roles)) return roles;
  if (roles == null) return [];
  return [roles];
}

// Union of every accessible menu id for the given role(s).
export function getAccessForRoles(roles) {
  const list = normalizeRoles(roles);
  let access = [];
  ROLE_ACCESS.forEach(([roleName, accessList]) => {
    if (list.includes(roleName)) {
      access = [...access, ...accessList];
    }
  });
  return access;
}

// Whether the role(s) may see a given menu id.
export function hasMenuAccess(roles, menuId) {
  return getAccessForRoles(roles).includes(menuId);
}

// First navigable top-level menu the role may land on after login.
// Skips external (token-exchange) menus, which have no local route.
// Falls back to "/" when nothing else matches.
export function getLandingRoute(roles) {
  const access = getAccessForRoles(roles);
  const landing = MENU_ITEMS.find(
    (item) => !item.external && access.includes(item.id),
  );
  return landing ? landing.url : "/";
}